// src/components/CookieConsent.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { loadGtag } from '../utils/gtag';

const COOKIE_KEY = 'cookie_consent';
const SETTINGS_KEY = 'cookie_settings';

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [customizeMode, setCustomizeMode] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    const settings = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}');

    if (consent === 'accepted') {
      loadGtag();
    } else if (consent === 'customized' && settings.analytics) {
      loadGtag();
    }

    if (!['accepted', 'declined', 'customized'].includes(consent)) {
      setOpen(true);
    }
    if (consent === 'customized') {
      setCustomizeMode(true);
      setAnalyticsEnabled(!!settings.analytics);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(COOKIE_KEY, 'accepted');
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({ analytics: true }));
    loadGtag();
    setOpen(false);
  };

  const handleDeclineAll = () => {
    localStorage.setItem(COOKIE_KEY, 'declined');
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({ analytics: false }));
    setOpen(false);
  };

  const handleCustomize = () => {
    setCustomizeMode(true);
    setAnalyticsEnabled(false);
  };

  const handleConfirm = () => {
    localStorage.setItem(COOKIE_KEY, 'customized');
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({ analytics: analyticsEnabled }));
    if (analyticsEnabled) loadGtag();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: { xs: '10%', sm: '20%' },
        width: { xs: '75%', sm: '50%' },
        bgcolor: 'background.paper',
        color: 'text.primary',
        p: 3,
        boxShadow: theme =>
          `0 -2px 12px ${
            theme.palette.mode === 'light' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.6)'
          }`,
        zIndex: theme => theme.zIndex.drawer + 1,
        borderRadius: 1,
      }}
    >
      <Typography
        variant="body2"
        align="center"
        sx={{ flex: 1, mb: customizeMode ? 2 : 3 }}
      >
        Ta strona korzysta z plików cookies, aby poprawić komfort użytkowania.
      </Typography>

      {!customizeMode && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button variant="outlined" onClick={handleDeclineAll}>
            Odrzucam
          </Button>
          <Button variant="outlined" onClick={handleCustomize}>
            Dostosuj
          </Button>
          <Button variant="contained" onClick={handleAcceptAll}>
            Akceptuję wszystkie
          </Button>
        </Box>
      )}

      {customizeMode && (
        <Box sx={{ width: '100%' }}>
          <FormGroup sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Switch checked disabled />}
              label="Pliki systemowe (zawsze włączone)"
            />
            <Typography variant="caption" sx={{ ml: 4, display: 'block', mb: 1 }}>
              • Niezbędne do działania serwisu
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={analyticsEnabled}
                  onChange={e => setAnalyticsEnabled(e.target.checked)}
                />
              }
              label="Pliki analityczne (Google Analytics)"
            />
            {/* Informacje o ciasteczkach GA */}
            <Box sx={{ ml: 4, mt: 0.5 }}>
              <Typography variant="caption" display="block">
                • _ga – przechowuje identyfikator użytkownika (2 lata)
              </Typography>
              <Typography variant="caption" display="block">
                • _gid – rozróżnia użytkowników (24 godziny)
              </Typography>
              <Typography variant="caption" display="block">
                • _gat – ogranicza liczbę żądań (1 minuta)
              </Typography>
            </Box>
          </FormGroup>

          <Divider />

          <Box sx={{ textAlign: 'right', mt: 2 }}>
            <Button variant="contained" onClick={handleConfirm}>
              Zatwierdź ustawienia
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
